import { depthChart, formatHeight, relativeTime } from '../../lib/dossier'
import type { NewsItem, PlayerSummary } from '../../types/protocol'

// Everything the app knows about one player, and nothing about where it is
// being shown. No hooks, no fetching, no panel chrome and no identity header:
// the phone's drawer and the laptop's scouting column are two containers around
// this one body, so the two surfaces cannot drift into showing different facts.
// The container owns layout; the body owns content.
//
// Every null field is omitted entirely — no em dashes, no "Unknown". That is
// the player-facing convention across the app (portrait_url, jersey_number and
// bye_week all already work this way); the admin table is the single place that
// prints a placeholder.

interface Props {
  player: PlayerSummary
  news: NewsItem[]
  newsLoading: boolean
  newsUnavailable: boolean
  /** How many items to show. The drawer is short on a phone. */
  newsLimit?: number
}

interface Vital {
  label: string
  value: string
  /** College is prose, not a measurement, so it does not get the mono grid. */
  prose?: boolean
}

export default function Dossier({
  player,
  news,
  newsLoading,
  newsUnavailable,
  newsLimit = 5,
}: Props) {
  const vitals: Vital[] = []
  const push = (label: string, value: string | null, prose = false) => {
    if (value !== null) vitals.push({ label, value, prose })
  }

  push('HT', formatHeight(player.height_inches))
  push('WT', player.weight_lbs === null ? null : `${player.weight_lbs} lb`)
  push('AGE', player.age === null ? null : `${player.age}`)
  // 0 is a rookie, and it is a real value that both `??` and a truthiness check
  // swallow — a rookie with no EXP pair is indistinguishable from a player
  // Sleeper has no data for.
  const exp = player.years_exp
  push('EXP', exp === null ? null : exp === 0 ? 'R' : `${exp} yr`)
  push('#', player.jersey_number === null ? null : `${player.jersey_number}`)
  push('BYE', player.bye_week === null ? null : `${player.bye_week}`)
  push('DEPTH', depthChart(player.depth_chart_position, player.depth_chart_order))
  // Sleeper's own designation, and independent of the pool's `active` filter —
  // Practice Squad and Inactive players are in the pool. "Active" is the
  // uninteresting majority, so it earns no pair of its own.
  const status = player.roster_status
  push('STATUS', status === null || status === 'Active' ? null : status)
  push('COLLEGE', player.college, true)

  const injurySince = relativeTime(player.injury_start_date)
  // The one and only place news_updated_at is ever rendered. It is as of the
  // last sync and the sync runs at most twice a day, so putting it anywhere
  // near a live news item would be a lie about freshness.
  const lastPosted = relativeTime(player.news_updated_at)

  return (
    <div className="space-y-3">
      {vitals.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {vitals.map((vital) => (
            <span key={vital.label}>
              <span className="font-mono text-[10px] tracking-wider text-ink-lo uppercase">
                {vital.label}
              </span>{' '}
              <span
                className={
                  vital.prose
                    ? 'font-sans text-[11px] text-ink-mid'
                    : 'font-mono text-[11px] text-ink-hi tabular-nums'
                }
              >
                {vital.value}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Gated on injury_status alone. roster_status is independent of the
          pool's active filter, so gating the crit container on a non-"Active"
          roster_status would put a red-bordered box around a healthy player
          whose only sin is being on a practice squad. */}
      {player.injury_status !== null && (
        <div className="space-y-1.5 rounded-md border border-crit/25 bg-crit/5 p-2.5">
          <p>
            <span className="rounded-full bg-crit/10 px-2 py-0.5 text-xs font-medium text-crit">
              {player.injury_status}
            </span>
            {player.injury_body_part !== null && (
              <span className="text-ink-mid"> · {player.injury_body_part}</span>
            )}
          </p>
          {player.practice_participation !== null && (
            <p className="text-[11px] text-ink-lo">{player.practice_participation}</p>
          )}
          {/* Unbounded free text from Sleeper, wrapped rather than truncated.
              React escapes it. */}
          {player.injury_notes !== null && (
            <p className="font-sans text-xs text-ink-mid">{player.injury_notes}</p>
          )}
          {injurySince !== null && (
            <p className="font-mono text-[10.5px] text-ink-lo">Since {injurySince}</p>
          )}
        </div>
      )}

      {newsUnavailable ? (
        // No header, no list, no error text, no spinner: unreachable renders as
        // absence. The one thing worth saying is when Sleeper last had anything
        // at all, and only if we know it.
        lastPosted !== null && (
          <p className="font-mono text-[10.5px] text-ink-lo">
            Sleeper last posted about this player {lastPosted}.
          </p>
        )
      ) : (
        <div className="space-y-1.5">
          <h3 className="eyebrow">Recent news</h3>
          {newsLoading ? (
            <p className="text-xs text-ink-lo">Checking for news…</p>
          ) : news.length === 0 ? (
            // Distinct from unavailable: this is the dossier saying it checked.
            <p className="text-xs text-ink-lo">No recent news.</p>
          ) : (
            <ul className="space-y-2.5">
              {news.slice(0, newsLimit).map((item) => (
                <NewsEntry key={item.id} item={item} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function NewsEntry({ item }: { item: NewsItem }) {
  const when = relativeTime(item.published_at)
  const meta = [item.source, when].filter((part) => part !== null).join(' · ')

  return (
    <li className="space-y-1">
      <p className="font-sans text-sm leading-snug font-semibold text-ink-hi">
        {/* No scheme check here: the server already emitted null for anything
            that is not an absolute https URL, and a second check would be a
            second place for that rule to rot. */}
        {item.url !== null ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline-offset-2 hover:text-signal hover:underline"
          >
            {item.title}
          </a>
        ) : (
          item.title
        )}
      </p>
      {meta !== '' && <p className="font-mono text-[10px] text-ink-lo">{meta}</p>}
      {item.description !== null && (
        <p className="font-sans text-xs leading-relaxed text-ink-mid">{item.description}</p>
      )}
      {item.analysis !== null && (
        <p className="border-l-2 border-line-strong pl-2 font-sans text-xs leading-relaxed text-ink-lo">
          {item.analysis}
        </p>
      )}
    </li>
  )
}
